import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';

import Button from '../../../components/Button/Button';
import { fetchLoginUserSummary } from '../../../application/auth/authSessionService';
import { writeViewerStoredUserSummary } from '../../auth/presentation/browserAuthSessionSupport';

type PendingApprovalLockProps = {
  workspaceName?: string | null;
};

/**
 * Full-surface lock shown over a workspace whose membership is PENDING owner
 * approval (a self-service volunteer/contributor join). The persona dashboard
 * renders underneath but is blurred + non-interactive until the owner approves
 * — at which point me/summary flips `is_pending_approval` to false and this
 * unmounts. Not dismissible; offers a status re-check and an escape hatch so
 * the user is never trapped.
 */
export const PendingApprovalLock = ({
  workspaceName
}: PendingApprovalLockProps) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);

  const org = (workspaceName || '').trim() || 'this organization';

  const handleCheckStatus = async () => {
    if (isChecking) return;
    setIsChecking(true);
    try {
      const summary = await fetchLoginUserSummary();
      if (summary) {
        writeViewerStoredUserSummary(summary);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('user-summary-updated', {
              detail: { summary }
            })
          );
        }
      }
    } catch (_) {
      // Non-blocking — the user can retry.
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Pending approval"
    >
      <div className="w-full max-w-md rounded-[20px] bg-white/95 p-8 text-center shadow-[0_10px_35px_rgba(0,0,0,0.2)] dark:bg-slate-800/95 dark:shadow-[0_10px_35px_rgba(0,0,0,0.5)]">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300">
          <FiClock className="text-2xl" />
        </div>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">
          Pending approval
        </p>
        <p className="mt-3 text-sm text-gray-600 dark:text-slate-300">
          Your request to join <span className="font-medium">{org}</span> is
          awaiting the organization&apos;s approval. We&apos;ll email you as
          soon as you&apos;re in — your dashboard unlocks automatically.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3">
          <Button
            type="button"
            size="normal"
            pill
            variant="secondary"
            className="rounded-2xl px-6"
            onClick={handleCheckStatus}
            isLoading={isChecking}
          >
            Check approval status
          </Button>
          <button
            type="button"
            className="text-sm text-gray-500 underline-offset-2 hover:underline dark:text-slate-400"
            onClick={() => navigate('/')}
          >
            Explore other organizations
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalLock;
